import React from 'react';
import { Table, TableBody, TableCell, TableRow, Typography } from '@rbx/ui';
import { useTranslation } from '@rbx/intl';
import { ReferenceMemberParameter } from '@modules/client';
import EngineMarkdown from '@modules/ui/markdown/components/EngineMarkdown';
import isTypeOptional from '@modules/utilities/pageBuild/isTypeOptional';
import { useReferenceTypesData } from './ReferenceTypesDataProvider';
import ReferenceMemberType from './ReferenceMemberType';
import SpaceSuffixedColon from './SpaceSuffixedColon';
import useReferenceMemberParametersTableStyles from './ReferenceMemberParametersTable.styles';

interface ReferenceMemberParametersTableProps {
  parameters: ReferenceMemberParameter[] | null;
}

function ReferenceMemberParametersTable({ parameters }: ReferenceMemberParametersTableProps) {
  const { translate } = useTranslation();
  const { classes } = useReferenceMemberParametersTableStyles();
  const { getTypeAliasDatum } = useReferenceTypesData();

  if (!parameters || parameters.length === 0) {
    return null;
  }

  return (
    <div className={classes.container}>
      <Typography variant='subtitle2' className={classes.title}>
        {translate('Label.Parameters') || 'Parameters'}
      </Typography>
      <Table className={classes.table}>
        <TableBody>
          {parameters.map((param) => {
            const { name, type, summary, default: defaultValue } = param;
            const hasDefaultValue =
              defaultValue !== null &&
              defaultValue.toString().trim().length !== 0 &&
              defaultValue !== 'null';

            const typeDatum = getTypeAliasDatum(type);
            if (!typeDatum) {
              throw new Error(`No type data found for ${type}`);
            }

            return (
              <TableRow key={name} className={classes.tableRow}>
                <TableCell>
                  <div className={classes.parameterName}>
                    <Typography component='span' variant='body2' className={classes.parameterText}>
                      {name}
                    </Typography>
                    <SpaceSuffixedColon engineRevampEnabled />
                    <ReferenceMemberType
                      parameter={typeDatum}
                      isOptional={isTypeOptional(type)}
                      engineRevampEnabled
                    />
                  </div>
                  {hasDefaultValue && (
                    <Typography variant='body2' className={classes.defaultValue}>
                      {translate('Label.DefaultValue', { defaultValue }) ||
                        `Default value: ${defaultValue}`}
                    </Typography>
                  )}
                  {summary && (
                    <EngineMarkdown
                      overrideParagraphStyle={{
                        marginTop: 4,
                        marginBottom: 0,
                      }}>
                      {summary}
                    </EngineMarkdown>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}

export default ReferenceMemberParametersTable;
